import type { Recurrence } from '../../shared/protocol';

/**
 * Share helpers — turn an event into a magic link, a copy-paste-ready message,
 * and one-tap deep links for every channel the crew actually uses.
 *
 * ALL links are built from SHARE_BASE, never window.location.origin — the SPA
 * is served at thursday-night.com/schedule through the front-door proxy, and
 * origin-based links used to land on the front door's own page (dropping the
 * /schedule base path and the query with it). VITE_SHARE_BASE is set at build
 * time and matches the worker's SITE_BASE_URL.
 */

export const SHARE_BASE: string = (
  import.meta.env.VITE_SHARE_BASE ?? 'https://thursday-night.com/schedule'
).replace(/\/$/, '');

/** Root of the front door (thursday-night.com) — for the /i/ short links. */
const FRONT_DOOR = SHARE_BASE.replace(/\/schedule$/, '');

/** The shareable event link. Optionally personalized with a voter code. */
export function eventLink(eventId: string, voterCode?: string): string {
  const u = new URL(`${SHARE_BASE}/`);
  u.searchParams.set('e', eventId);
  if (voterCode) u.searchParams.set('c', voterCode);
  return u.toString();
}

/** The host's private dashboard link (carries the host token — keep it secret). */
export function hostLink(eventId: string, hostToken: string): string {
  const u = new URL(`${SHARE_BASE}/`);
  u.searchParams.set('e', eventId);
  u.searchParams.set('host', hostToken);
  return u.toString();
}

/**
 * The short invite/reminder link the worker texts and emails — the front-door
 * worker 302s `/i/:code` to `/schedule/?i=:code`, which this SPA resolves back
 * to the voter page. One link, every channel, fits in a GSM-7 text.
 */
export function inviteShortLink(voterCode: string): string {
  return `${FRONT_DOOR}/i/${voterCode}`;
}

/** A guide link (?g=host|vr|browser), optionally carrying a literal room code to display. */
export function guideLink(guide: 'host' | 'vr' | 'browser', roomCode?: string): string {
  const u = new URL(`${SHARE_BASE}/`);
  u.searchParams.set('g', guide);
  if (roomCode) u.searchParams.set('room', roomCode);
  return u.toString();
}

/**
 * The 32 Crockford symbols the game's encoder actually emits, copied from
 * BFNRoomCode.h — digits 0-9 included, letters excluding I, L, O and U. A room
 * code is a host IPv4 in this base32, so every symbol turns up in real codes.
 *
 * THU-75. This used to be `A-Z2-9`, which is wrong in both directions: it
 * REJECTED 0 and 1, which the encoder emits whenever the address demands them,
 * and ACCEPTED I/L/O/U, which it never can. That is the identical drift THU-72
 * found and fixed in the worker's roomPosters.ts, in a third copy the guard
 * didn't reach - and this file called itself a "mirror" the whole time.
 *
 * What it cost, on the guest's ballot page: placeToRoomCode returned null for
 * roughly a third of all real codes ((30/32)^7 ~ 64% carry neither digit), so
 * VotePage showed the "YOUR ROOM CODE / it's in your invite text" placeholder
 * instead of the code, dropped the copy button and the PC join button, and
 * stripped &room= from both "Join in VR" and "Watch in your browser". The night
 * still worked for anyone who read the code off their invite text, which is why
 * this was survivable and invisible: it fails for a THIRD of nights, so it
 * works every time you happen to test it.
 */
const ROOM_CODE_SYMBOLS = /^[0-9ABCDEFGHJKMNPQRSTVWXYZ]+$/;

/**
 * Mirror of the worker's placeToRoomCode: pull a joinable room code out of the
 * free-text place (a ?room= param, or the string stripped to the Crockford
 * symbols, 4-16 chars). Hyphens are kept in the returned string - the worker
 * strips them because it keys storage rows by code, this keeps them because it
 * is what gets shown to a person.
 */
export function placeToRoomCode(place: string): string | null {
  try {
    const u = new URL(place);
    const q = u.searchParams.get('room');
    if (q) place = q;
  } catch {
    // not a URL — treat as a raw code
  }
  const code = place.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  const stripped = code.replace(/-/g, '');
  if (stripped.length >= 4 && stripped.length <= 16 && ROOM_CODE_SYMBOLS.test(stripped)) {
    return code;
  }
  return null;
}

export function recurrenceText(r: Recurrence): string {
  if (r.freq === 'none') return '';
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const day = r.weekday !== undefined ? days[r.weekday] : '';
  switch (r.freq) {
    case 'weekly':
      return `every week${day ? ` on ${day}` : ''}`;
    case 'biweekly':
      return `every other week${day ? ` on ${day}` : ''}`;
    case 'monthly':
      return 'every month';
    default:
      return '';
  }
}

export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface InviteMessageParts {
  title: string;
  startsAt: string;
  place: string;
  recurrence: Recurrence;
  link: string;
  note?: string;
}

/** The copy-paste-ready formatted message. Plain text, reads well anywhere. */
export function inviteMessage(p: InviteMessageParts): string {
  const recur = recurrenceText(p.recurrence);
  const lines = [
    `🍿 Thursday Night: ${p.title}`,
    ``,
    `When: ${formatWhen(p.startsAt)}${recur ? ` (${recur})` : ''}`,
    `Where: ${p.place}`,
  ];
  if (p.note) lines.push(`Note: ${p.note}`);
  lines.push(``, `Vote on what we watch + join here:`, p.link);
  lines.push(`New here? How to join: ${guideLink('browser')}`);
  return lines.join('\n');
}

export interface DeepLinks {
  discord: string; // opens Discord (web) with text in clipboard-friendly form
  sms: string;
  whatsapp: string;
  email: string;
  telegram: string;
}

/** One-tap deep links for each channel. */
export function deepLinks(message: string, subject: string): DeepLinks {
  const enc = encodeURIComponent(message);
  return {
    // Discord has no message-prefill deep link; we open the app and the user
    // pastes (the UI also copies the message for them). This is the documented
    // limitation — Discord intentionally has no compose-with-text URL scheme.
    discord: 'https://discord.com/channels/@me',
    sms: `sms:?&body=${enc}`,
    whatsapp: `https://wa.me/?text=${enc}`,
    email: `mailto:?subject=${encodeURIComponent(subject)}&body=${enc}`,
    telegram: `https://t.me/share/url?url=${enc}`,
  };
}

/** Web Share API target (mobile/PWA). Returns false if unsupported. */
export async function nativeShare(title: string, text: string, url: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      // user cancelled or share failed — fall back to copy
      return false;
    }
  }
  return false;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
